import {
  Box,
  Button,
  ButtonProps,
  Container,
  CopyToClipboard,
  Header,
  SpaceBetween,
} from "@amzn/awsui-components-console";
import React, { useCallback, useRef, useState } from "react";
import { format } from "sql-formatter";

import useAdminCachedMessageSqlEdit from "@/hooks/admin/useAdminCachedMessageSqlEdit";

import { MessageItem } from "../types";
import SQLEditModal from "./SQLEditModal";

export interface SQLQuerySectionProps {
  query: string;
  /** The cache entry the query belongs to, and the target of an edit. */
  messageId: string;
  /**
   * Called with the entry as the server stored it, after an accepted edit.
   *
   * Fires for a partial success too -- the query was stored either way, and the
   * caller needs the server's entry to re-render the row it just changed.
   */
  onSqlUpdated?: (entry: MessageItem) => void;
}

const NUM_OF_LINES = 5;

const SQLQuerySection: React.FC<SQLQuerySectionProps> = ({
  query,
  messageId,
  onSqlUpdated,
}) => {
  const [collapse, setCollapse] = useState(true);
  const toggleCollapse = () => setCollapse((prev) => !prev);

  const [editing, setEditing] = useState(false);
  // Read imperatively so the modal can return focus here on close, never
  // rendered.
  const editButtonRef = useRef<ButtonProps.Ref>(null);

  const { updateSqlQuery } = useAdminCachedMessageSqlEdit();

  /**
   * Hands the modal a promise whose outcome decides whether it closes.
   *
   * `updateSqlQuery` rejects for a failure, so a rejection propagates and the
   * modal stays open with the admin's text intact. It resolves for a partial
   * success, so the modal closes and the entry is adopted -- the query was
   * stored, and the page has to show that.
   */
  const handleSubmit = useCallback(
    async (sqlQuery: string) => {
      const outcome = await updateSqlQuery(messageId, sqlQuery);
      if (outcome.kind !== "failed") {
        onSqlUpdated?.(outcome.entry);
      }
    },
    [updateSqlQuery, messageId, onSqlUpdated],
  );

  let formattedQuery: string;
  try {
    formattedQuery = format(query, { language: "postgresql" });
  } catch {
    // Fall back to unformatted if the parser can't handle the SQL syntax
    formattedQuery = query;
  }

  const lines = formattedQuery.split("\n");

  return (
    <Container
      header={
        <Header
          actions={
            <SpaceBetween size="s" direction="horizontal">
              <CopyToClipboard
                copyButtonText="Copy"
                copyErrorText="SQL failed to copy"
                copySuccessText="SQL copied"
                textToCopy={query}
              />
              {/*
                A real Cloudscape Button with text, deliberately not a
                role-annotated element: keyboard focusability and Enter/Space
                activation then come from the native button, so no ARIA promise
                is made that the markup does not actually keep.

                Rendered for every entry -- any cache status, and whether or not
                the stored query is empty -- because an entry with no SQL is
                exactly one an admin needs to be able to fix.
              */}
              <Button
                ref={editButtonRef}
                onClick={() => setEditing(true)}
                data-testid="edit-sql-button"
              >
                Edit SQL
              </Button>
            </SpaceBetween>
          }
        >
          Generated SQL
        </Header>
      }
    >
      <Box variant="code" data-testid="code-box">
        <pre>
          {lines.length > NUM_OF_LINES && collapse
            ? `${lines.slice(0, NUM_OF_LINES).join("\n")}\n...`
            : formattedQuery}
        </pre>
      </Box>
      {lines.length > NUM_OF_LINES &&
        (collapse ? (
          <Box variant="awsui-gen-ai-label" data-testid="expand-box">
            Showing the first {NUM_OF_LINES} lines of the SQL query.{" "}
            <Button
              variant="inline-link"
              onClick={toggleCollapse}
              data-testid="expand-button"
            >
              Expand
            </Button>
          </Box>
        ) : (
          <Button
            variant="inline-link"
            onClick={toggleCollapse}
            data-testid="collapse-button"
          >
            Collapse
          </Button>
        ))}
      {/*
        Seeded from `query`, the stored value this section was handed -- never
        from `formattedQuery` or the truncated preview above it.
      */}
      <SQLEditModal
        visible={editing}
        sqlQuery={query}
        onDismiss={() => setEditing(false)}
        onSubmit={handleSubmit}
        returnFocusRef={editButtonRef}
      />
    </Container>
  );
};

export default SQLQuerySection;
